/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Linkable } from '@/components/blocks/Linkable';
import { HeaderAccountDropMenu } from '@/components/content/Header/parts/AccountDropMenu';
import { headerAccountContainerSX } from '@/components/content/Header/styles/account/container';
import { headerItemStackSX } from '@/components/content/Header/styles/itemStack';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { usePreSelectContract } from '@/data/Content/PreSelectContract';
import { useLocalization } from '@/data/Localization';
import { useUserCheckLoggedIn } from '@/data/User';
import { useThemeSettings } from '@/styles/theme';
import { Switch } from '@/utils/switch';
import PersonIcon from '@mui/icons-material/Person';
import { Breakpoint, Button, ClickAwayListener, Stack, Tooltip } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { headerLinkSX } from '@/components/content/Header/styles/link';
import { useMobileHeaderQuery } from '..';

type Props = {
	mobileBreakpoint: Breakpoint;
};

export const HeaderAccount: FC<Props> = () => {
	const AccountLabels = useLocalization('SignInPage');
	const HeaderLabels = useLocalization('Header');
	const RouteLocal = useLocalization('Routes');
	const router = useNextRouter();
	const { getAdditive } = useThemeSettings();
	const isMobile = useMobileHeaderQuery();
	const [open, setOpen] = useState(false);
	const { user, isLoggedIn } = useUserCheckLoggedIn();

	const welcomeMessage = isLoggedIn
		? user?.firstName
			? HeaderLabels.Actions.WelcomeFirstName.t({ firstName: user?.firstName })
			: HeaderLabels.Actions.WelcomeNoFirstName.t()
		: AccountLabels.Title.t();

	usePreSelectContract();

	const handleToolTip = useCallback(
		(action?: string) => () =>
			setOpen((open) =>
				Switch(action)
					.case('open', () => true)
					.case('close', () => false)
					.defaultTo(() => !open)
			),
		[]
	);
	useEffect(() => {
		if (!router.asPath) return;
		handleToolTip('close')();
	}, [handleToolTip, router.asPath]);

	return (
		<ClickAwayListener onClickAway={handleToolTip('close')}>
			<Stack sx={headerAccountContainerSX}>
				<Tooltip
					placement="bottom"
					open={open}
					onClose={handleToolTip('close')}
					disableFocusListener
					disableHoverListener
					disableTouchListener
					PopperProps={{
						sx: {
							width: '100%',
							backgroundColor: 'rgba(0, 0, 0, 0.5)',
							opacity: '1',
							height: '100%',
						},
					}}
					componentsProps={{
						transition: {
							sx: {
								marginRight: '20px',
								backgroundColor: 'transparent',
								color: '#2C2C2C',
								float: 'right',
							},
						} as any,
					}}
					title={isLoggedIn ? <HeaderAccountDropMenu onClose={handleToolTip('close')} /> : null}
				>
					{isLoggedIn ? (
						<Stack alignItems="center" sx={headerItemStackSX} onClick={handleToolTip()}>
							<PersonIcon />
						</Stack>
					) : (
						<Stack alignItems="center" sx={headerItemStackSX}>
							<Linkable
								aria-label={welcomeMessage}
								sx={headerLinkSX}
								href={{
									pathname: `/${RouteLocal.Login.route.t()}`,
									query: { origin: 'go_to_account' },
								}}
								id="sign-in-or-account-route-icon"
								data-testid="sin-in-or-account-route-icon"
							>
								<PersonIcon />
							</Linkable>
						</Stack>
					)}
				</Tooltip>
				{isMobile === false && isLoggedIn ? (
					<Button sx={getAdditive('coverTapTarget')} onClick={handleToolTip()}>
						{welcomeMessage}
					</Button>
				) : null}
			</Stack>
		</ClickAwayListener>
	);
};
